import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";

const SCRIPT_ID = "recaptcha-api";
const SCRIPT_SRC =
    "https://www.google.com/recaptcha/api.js?render=explicit&onload=__starekRecaptchaReady";

// Loads Google's reCAPTCHA api.js once per page and resolves when the global
// is ready. Explicit render mode keeps the widget under our control so it can
// live inside a React tree and be reset between attempts.
let readyPromise = null;
const loadRecaptcha = () => {
    if (readyPromise) return readyPromise;

    readyPromise = new Promise((resolve, reject) => {
        if (window.grecaptcha?.render) {
            resolve(window.grecaptcha);
            return;
        }

        window.__starekRecaptchaReady = () => resolve(window.grecaptcha);

        if (document.getElementById(SCRIPT_ID)) return;

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onerror = () =>
            reject(new Error("Could not load reCAPTCHA. Check your network."));
        document.head.appendChild(script);
    });

    return readyPromise;
};

/**
 * reCAPTCHA v2 checkbox.
 *
 * Calls onChange with a token when solved, and with null when the token
 * expires or the user clears it. Tokens are single-use — call reset() through
 * the ref after every submit that consumed one.
 */
const Recaptcha = forwardRef(function Recaptcha(
    { siteKey, theme = "light", onChange, onError },
    ref
) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    // Kept in refs so the callbacks handed to grecaptcha (which are bound once,
    // at render time) always reach the latest props.
    const onChangeRef = useRef(onChange);
    const onErrorRef = useRef(onError);
    onChangeRef.current = onChange;
    onErrorRef.current = onError;

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (widgetIdRef.current === null) return;
            window.grecaptcha?.reset(widgetIdRef.current);
            onChangeRef.current?.(null);
        },
    }));

    useEffect(() => {
        if (!siteKey) return;

        let cancelled = false;

        loadRecaptcha()
            .then((grecaptcha) => {
                if (cancelled || widgetIdRef.current !== null) return;
                if (!containerRef.current) return;

                widgetIdRef.current = grecaptcha.render(containerRef.current, {
                    sitekey: siteKey,
                    theme,
                    callback: (token) => onChangeRef.current?.(token),
                    "expired-callback": () => onChangeRef.current?.(null),
                    "error-callback": () => {
                        onChangeRef.current?.(null);
                        onErrorRef.current?.(
                            new Error("reCAPTCHA could not be completed.")
                        );
                    },
                });
            })
            .catch((e) => {
                if (cancelled) return;
                console.error("[Recaptcha] failed to load:", e);
                onErrorRef.current?.(e);
            });

        return () => {
            cancelled = true;
        };
    }, [siteKey, theme]);

    if (!siteKey) return null;

    return (
        <div
            className="d-flex justify-content-center mb-3"
            ref={containerRef}
            data-testid="recaptcha"
        />
    );
});

export default Recaptcha;
